import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWebSocketEvent } from './useWebSocketEvent';
import { useInvalidateCustomerState } from './useMspCustomers';
import {
  WebSocketEventType,
  DeviceStatusChangedPayload,
  DeviceCreatedPayload,
  DeviceDeletedPayload,
  DeviceUpdatedPayload,
  DeviceDiscoveredPayload,
  InterfaceUpdatedPayload,
  ConnectionDiscoveredPayload,
  ConnectionRemovedPayload,
  GatewayStatusChangedPayload,
  GatewaySoftwareStatusChangedPayload,
  GatewaySoftwareProgressPayload,
  GatewayUpdatedPayload,
  GatewayDeletedPayload,
  GatewayDetectedPayload,
  CredentialCreatedPayload,
  CredentialDeletedPayload,
  DivisionCreatedPayload,
  DivisionDeletedPayload,
  RegionCreatedPayload,
  RegionDeletedPayload,
  PlanEventPayload,
  DeviceScopedEventPayload,
  TopologyEdgeChangedPayload,
  EndpointEventPayload,
  CmEventPayload,
} from '../types/websocket';

/**
 * Hook that automatically syncs WebSocket events with React Query cache
 *
 * This implements the "Event Notification + REST Fetch" pattern:
 * 1. WebSocket sends lightweight notification: "device X changed"
 * 2. React Query cache is invalidated for that data
 * 3. Active queries automatically refetch fresh data from REST API
 *
 * This hook should be used at the App level or in specific pages that need
 * real-time updates.
 *
 * @example
 * ```tsx
 * // In App.tsx or Views.tsx
 * useWebSocketCacheSync();
 * ```
 */
export function useWebSocketCacheSync() {
  const queryClient = useQueryClient();

  // === Device Events ===

  // Device status changed -> Invalidate device queries
  //
  // NOTE: nothing publishes `device.status.changed` today — the backend reports a
  // status flip as `device.updated` (see the DEVICE_UPDATED handler below, and
  // `report_device_status` in website-backend, which emits it whenever the status,
  // classification or hardware identity changed). This subscription is kept
  // because it costs nothing and is correct if that subject is ever emitted, but
  // do not reach for it when debugging why a status did not refresh: DEVICE_UPDATED
  // is the live path.
  useWebSocketEvent<DeviceStatusChangedPayload>(
    WebSocketEventType.DEVICE_STATUS_CHANGED,
    (payload) => {
      console.log('[WebSocket] Device status changed:', payload.deviceId);

      // Invalidate queries that might include this device
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
      void queryClient.invalidateQueries({ queryKey: ['devices', payload.deviceId] });
      // ...and the device DETAIL record, which lives under a different key root.
      // See the note above `useDeviceDetails` about the two namespaces.
      void queryClient.invalidateQueries({ queryKey: ['device', payload.deviceId] });

      // If your Views page queries devices by region
      if (payload.regionId) {
        void queryClient.invalidateQueries({
          queryKey: ['views', 'data', payload.regionId],
        });
      }
    }
  );

  // Device created -> Invalidate device list queries
  useWebSocketEvent<DeviceCreatedPayload>(
    WebSocketEventType.DEVICE_CREATED,
    (payload) => {
      console.log('[WebSocket] Device created:', payload.deviceId);

      void queryClient.invalidateQueries({ queryKey: ['devices'] });

      if (payload.regionId) {
        void queryClient.invalidateQueries({
          queryKey: ['views', 'data', payload.regionId],
        });
      }
    }
  );

  // Device deleted -> Invalidate device queries
  useWebSocketEvent<DeviceDeletedPayload>(
    WebSocketEventType.DEVICE_DELETED,
    (payload) => {
      console.log('[WebSocket] Device deleted:', payload.deviceId);

      void queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.removeQueries({ queryKey: ['devices', payload.deviceId] });
      // Drop the detail record as well, or a deleted device keeps rendering from
      // cache for anyone sitting on its page.
      queryClient.removeQueries({ queryKey: ['device', payload.deviceId] });

      if (payload.regionId) {
        void queryClient.invalidateQueries({
          queryKey: ['views', 'data', payload.regionId],
        });
      }
    }
  );

  // Device updated -> Invalidate device queries
  useWebSocketEvent<DeviceUpdatedPayload>(
    WebSocketEventType.DEVICE_UPDATED,
    (payload) => {
      console.log('[WebSocket] Device updated:', payload.deviceId);

      void queryClient.invalidateQueries({ queryKey: ['devices'] });
      void queryClient.invalidateQueries({ queryKey: ['devices', payload.deviceId] });
      // The device detail record — `useDeviceDetails` keys on ['device', id, ...],
      // which the plural key above does NOT prefix-match. Without this, a status
      // flip refreshed every list in the product except the page showing that one
      // device, whose chip then waited out a 30s staleTime plus a refetch trigger
      // (refocus / remount) before catching up.
      void queryClient.invalidateQueries({ queryKey: ['device', payload.deviceId] });

      if (payload.regionId) {
        void queryClient.invalidateQueries({
          queryKey: ['views', 'data', payload.regionId],
        });
      }
    }
  );

  // Device discovered -> Invalidate device queries
  useWebSocketEvent<DeviceDiscoveredPayload>(
    WebSocketEventType.DEVICE_DISCOVERED,
    (payload) => {
      console.log('[WebSocket] Device discovered:', payload.deviceId);

      void queryClient.invalidateQueries({ queryKey: ['devices'] });

      if (payload.regionId) {
        void queryClient.invalidateQueries({
          queryKey: ['views', 'data', payload.regionId],
        });
      }
    }
  );

  // === Interface Events ===

  // Interface updated -> Invalidate device and interface queries
  useWebSocketEvent<InterfaceUpdatedPayload>(
    WebSocketEventType.INTERFACE_UPDATED,
    (payload) => {
      console.log('[WebSocket] Interface updated for device:', payload.deviceId);

      // Invalidate device detail query (which includes interfaces)
      void queryClient.invalidateQueries({ queryKey: ['device', payload.deviceId] });

      // Invalidate device list (in case interface changes affect the list view)
      void queryClient.invalidateQueries({ queryKey: ['devices'] });

      // Invalidate topology view if in a region
      if (payload.regionId) {
        void queryClient.invalidateQueries({
          queryKey: ['views', 'data', payload.regionId],
        });
      }
    }
  );

  // === Connection/Topology Events ===

  // Connection discovered -> Invalidate topology queries
  useWebSocketEvent<ConnectionDiscoveredPayload>(
    WebSocketEventType.CONNECTION_DISCOVERED,
    (payload) => {
      console.log('[WebSocket] Connection discovered:', payload.connectionId);

      void queryClient.invalidateQueries({ queryKey: ['connections'] });

      if (payload.regionId) {
        void queryClient.invalidateQueries({
          queryKey: ['views', 'data', payload.regionId],
        });
      }
    }
  );

  // Connection removed -> Invalidate topology queries
  useWebSocketEvent<ConnectionRemovedPayload>(
    WebSocketEventType.CONNECTION_REMOVED,
    (payload) => {
      console.log('[WebSocket] Connection removed:', payload.connectionId);

      void queryClient.invalidateQueries({ queryKey: ['connections'] });
      queryClient.removeQueries({ queryKey: ['connections', payload.connectionId] });

      if (payload.regionId) {
        void queryClient.invalidateQueries({
          queryKey: ['views', 'data', payload.regionId],
        });
      }
    }
  );

  // === Gateway Events ===

  // Gateway status changed -> Invalidate gateway queries
  useWebSocketEvent<GatewayStatusChangedPayload>(
    WebSocketEventType.GATEWAY_STATUS_CHANGED,
    (payload) => {
      // console.log('[WebSocket] Gateway status changed:', payload.gatewayId);

      void queryClient.invalidateQueries({ queryKey: ['gateways'] });
      void queryClient.invalidateQueries({ queryKey: ['gateways', payload.gatewayId] });
    }
  );

  // Gateway software deployment status changed (converging/applied/failed) ->
  // refresh the version panel + gateway row live, so the update dialog shows
  // progress without polling (the version column carries update_status_phase).
  useWebSocketEvent<GatewaySoftwareStatusChangedPayload>(
    WebSocketEventType.GATEWAY_SOFTWARE_STATUS_CHANGED,
    (payload) => {
      void queryClient.invalidateQueries({ queryKey: ['gateway-software-versions', payload.gatewayId] });
      void queryClient.invalidateQueries({ queryKey: ['gateways'] });
      void queryClient.invalidateQueries({ queryKey: ['gateways', payload.gatewayId] });
    }
  );

  // Live install progress -> refresh only the version panel. Deliberately not
  // invalidating ['gateways']: this arrives every few seconds for the length of
  // an install, and the list has nothing on it that progress changes.
  useWebSocketEvent<GatewaySoftwareProgressPayload>(
    WebSocketEventType.GATEWAY_SOFTWARE_PROGRESS,
    (payload) => {
      void queryClient.invalidateQueries({ queryKey: ['gateway-software-versions', payload.gatewayId] });
    }
  );

  // Gateway updated -> Invalidate gateway queries
  useWebSocketEvent<GatewayUpdatedPayload>(
    WebSocketEventType.GATEWAY_UPDATED,
    (payload) => {
      console.log('[WebSocket] Gateway updated:', payload.gatewayId);

      void queryClient.invalidateQueries({ queryKey: ['gateways'] });
      void queryClient.invalidateQueries({ queryKey: ['gateways', payload.gatewayId] });
    }
  );

  // Gateway deleted -> Invalidate gateway queries
  useWebSocketEvent<GatewayDeletedPayload>(
    WebSocketEventType.GATEWAY_DELETED,
    (payload) => {
      console.log('[WebSocket] Gateway deleted:', payload.gatewayId);

      void queryClient.invalidateQueries({ queryKey: ['gateways'] });
      queryClient.removeQueries({ queryKey: ['gateways', payload.gatewayId] });
    }
  );

  // Gateway detected -> Invalidate available gateways query
  // Backend filters by claim code, so we only receive events for our active claim session
  useWebSocketEvent<GatewayDetectedPayload>(
    WebSocketEventType.GATEWAY_DETECTED,
    (payload) => {
      // console.log('[WebSocket] Gateway detected for claim code:', payload.claimCode, payload.gateway.serial);
      void queryClient.invalidateQueries({ queryKey: ['available-gateways', payload.claimCode] });
    }
  );

  // === Credential Events ===

  // Credential created -> Invalidate credential queries
  useWebSocketEvent<CredentialCreatedPayload>(
    WebSocketEventType.CREDENTIAL_CREATED,
    (payload) => {
      console.log('[WebSocket] Credential created:', payload.credentialId);

      void queryClient.invalidateQueries({ queryKey: ['credentials'] });
    }
  );

  // Credential deleted -> Invalidate credential queries
  useWebSocketEvent<CredentialDeletedPayload>(
    WebSocketEventType.CREDENTIAL_DELETED,
    (payload) => {
      console.log('[WebSocket] Credential deleted:', payload.credentialId);

      void queryClient.invalidateQueries({ queryKey: ['credentials'] });
      queryClient.removeQueries({ queryKey: ['credentials', payload.credentialId] });
    }
  );

  // === Organization Structure Events ===

  // Division created -> Invalidate division queries
  useWebSocketEvent<DivisionCreatedPayload>(
    WebSocketEventType.DIVISION_CREATED,
    (payload) => {
      console.log('[WebSocket] Division created:', payload.divisionId);

      void queryClient.invalidateQueries({ queryKey: ['divisions'] });
    }
  );

  // Division deleted -> Invalidate division queries
  useWebSocketEvent<DivisionDeletedPayload>(
    WebSocketEventType.DIVISION_DELETED,
    (payload) => {
      console.log('[WebSocket] Division deleted:', payload.divisionId);

      void queryClient.invalidateQueries({ queryKey: ['divisions'] });
      queryClient.removeQueries({ queryKey: ['divisions', payload.divisionId] });
    }
  );

  // Region created -> Invalidate region queries
  useWebSocketEvent<RegionCreatedPayload>(
    WebSocketEventType.REGION_CREATED,
    (payload) => {
      console.log('[WebSocket] Region created:', payload.regionId);

      void queryClient.invalidateQueries({ queryKey: ['regions'] });
    }
  );

  // Region deleted -> Invalidate region queries
  useWebSocketEvent<RegionDeletedPayload>(
    WebSocketEventType.REGION_DELETED,
    (payload) => {
      console.log('[WebSocket] Region deleted:', payload.regionId);

      void queryClient.invalidateQueries({ queryKey: ['regions'] });
      queryClient.removeQueries({ queryKey: ['regions', payload.regionId] });
    }
  );

  // === Banner Events ===

  // Banner update -> Refetch banner data
  useWebSocketEvent(
    WebSocketEventType.BANNER_UPDATE,
    () => {
      console.log('[WebSocket] Banner update received');
      void queryClient.invalidateQueries({ queryKey: ['banners'] });
    }
  );

  // === Network Plan Events ===

  const invalidatePlanQueries = (payload: PlanEventPayload) => {
    void queryClient.invalidateQueries({ queryKey: ['network-plan', payload.resourceId] });
    void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    // A plan now lives embedded in its CM: step/execution changes also move the CM's
    // aggregate progress, timeline (step_* events), plan cards, and list progress column.
    // The plan payload has no cmId, so invalidate the CM query prefixes — only the
    // currently-mounted CM views refetch.
    void queryClient.invalidateQueries({ queryKey: ['cm-plans'] });
    void queryClient.invalidateQueries({ queryKey: ['cm-events'] });
    void queryClient.invalidateQueries({ queryKey: ['change-management'] });
    void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
  };

  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_EXECUTION_STARTED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_STEP_STARTED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_STEP_COMPLETED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_STEP_FAILED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_STEP_SKIPPED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_COMPLETED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_FAILED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_CANCELLED, invalidatePlanQueries);
  // Plan CRUD + step edits (complementing the execution events above).
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_CREATED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_UPDATED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_DELETED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_ARCHIVED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_RETRIED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_STEP_CREATED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_STEP_UPDATED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_STEP_DELETED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_STEP_REORDERED, invalidatePlanQueries);
  useWebSocketEvent<PlanEventPayload>(WebSocketEventType.PLAN_STEP_RETRIED, invalidatePlanQueries);

  // === Device sub-resource events (gateway-reported) ===
  // Each carries { deviceId, regionId? }. Invalidate the specific device-detail
  // tab query so the live view refetches, plus the region topology/views where
  // relevant.

  // Wireless: client associate/roam/disassociate — the "who's on which AP" view.
  useWebSocketEvent<DeviceScopedEventPayload>(WebSocketEventType.WIRELESS_CLIENT_CHANGED, (p) => {
    void queryClient.invalidateQueries({ queryKey: ['deviceWireless', p.deviceId] });
    void queryClient.invalidateQueries({ queryKey: ['wireless-clients'] });
  });

  // VLANs.
  useWebSocketEvent<DeviceScopedEventPayload>(WebSocketEventType.DEVICE_VLANS_CHANGED, (p) => {
    void queryClient.invalidateQueries({ queryKey: ['deviceVlans', p.deviceId] });
  });

  // Routing table (KairosDB-backed; the event is a refetch nudge from metric ingest).
  useWebSocketEvent<DeviceScopedEventPayload>(WebSocketEventType.DEVICE_ROUTES_CHANGED, (p) => {
    void queryClient.invalidateQueries({ queryKey: ['deviceRoutes', p.deviceId] });
    void queryClient.invalidateQueries({ queryKey: ['deviceRoutesHistory', p.deviceId] });
  });

  // STP state / inconsistencies — overlay is region-keyed; also bump device detail.
  const invalidateStp = (p: DeviceScopedEventPayload) => {
    void queryClient.invalidateQueries({ queryKey: ['stpOverlay'] });
    void queryClient.invalidateQueries({ queryKey: ['device', p.deviceId] });
  };
  useWebSocketEvent<DeviceScopedEventPayload>(WebSocketEventType.DEVICE_STP_CHANGED, invalidateStp);
  useWebSocketEvent<DeviceScopedEventPayload>(WebSocketEventType.DEVICE_STP_INCONSISTENCY_CHANGED, invalidateStp);

  // Collector / connection-method health — the device Health tab + Connections tab.
  useWebSocketEvent<DeviceScopedEventPayload>(WebSocketEventType.DEVICE_COLLECTOR_HEALTH_CHANGED, (p) => {
    void queryClient.invalidateQueries({ queryKey: ['device-health', p.deviceId] });
  });
  useWebSocketEvent<DeviceScopedEventPayload>(WebSocketEventType.DEVICE_CONNECTION_HEALTH_CHANGED, (p) => {
    void queryClient.invalidateQueries({ queryKey: ['device-health', p.deviceId] });
    void queryClient.invalidateQueries({ queryKey: ['connectionMethods', p.deviceId] });
  });

  // Security policy (device/interface scoped).
  const invalidateDeviceSecurityPolicy = (p: DeviceScopedEventPayload) => {
    void queryClient.invalidateQueries({ queryKey: ['security-policy'] });
    void queryClient.invalidateQueries({ queryKey: ['allowed-endpoints'] });
    void queryClient.invalidateQueries({ queryKey: ['effective-security-mode'] });
    void queryClient.invalidateQueries({ queryKey: ['device', p.deviceId] });
  };
  useWebSocketEvent<DeviceScopedEventPayload>(WebSocketEventType.DEVICE_SECURITY_POLICY_UPDATED, invalidateDeviceSecurityPolicy);
  useWebSocketEvent<DeviceScopedEventPayload>(WebSocketEventType.DEVICE_SECURITY_POLICY_ALLOWED_CHANGED, invalidateDeviceSecurityPolicy);

  // Config snapshot captured / OID dump created.
  useWebSocketEvent<DeviceScopedEventPayload>(WebSocketEventType.DEVICE_CONFIG_SNAPSHOT_CREATED, (p) => {
    void queryClient.invalidateQueries({ queryKey: ['configSnapshots', p.deviceId] });
  });
  // A restore moved on (queued → running → done). A finished one is followed
  // by a fresh capture on the server, which publishes no snapshot event of its
  // own, so the timeline is refreshed here too.
  // A change's rollback restores lists them too.
  useWebSocketEvent<DeviceScopedEventPayload>(WebSocketEventType.DEVICE_CONFIG_RESTORE_UPDATED, (p) => {
    void queryClient.invalidateQueries({ queryKey: ['configRestores', p.deviceId] });
    void queryClient.invalidateQueries({ queryKey: ['configSnapshots', p.deviceId] });
    void queryClient.invalidateQueries({ queryKey: ['cm-restores'] });
  });
  useWebSocketEvent<DeviceScopedEventPayload>(WebSocketEventType.DEVICE_OID_DUMP_CREATED, (p) => {
    void queryClient.invalidateQueries({ queryKey: ['oidDumps', p.deviceId] });
  });

  // === Topology edge changed (replaces the never-emitted connection.* events) ===
  useWebSocketEvent<TopologyEdgeChangedPayload>(WebSocketEventType.TOPOLOGY_EDGE_CHANGED, (p) => {
    void queryClient.invalidateQueries({ queryKey: ['topology'] });
    void queryClient.invalidateQueries({ queryKey: ['connections'] });
    void queryClient.invalidateQueries({ queryKey: ['device-connections'] });
    if (p.regionId) {
      void queryClient.invalidateQueries({ queryKey: ['views', 'data', p.regionId] });
    }
  });

  // === Endpoint events ===
  const invalidateEndpoints = (p: EndpointEventPayload) => {
    void queryClient.invalidateQueries({ queryKey: ['endpoints'] });
    void queryClient.invalidateQueries({ queryKey: ['endpointsForDevice'] });
    if (p.endpointId) {
      void queryClient.invalidateQueries({ queryKey: ['endpoint', p.endpointId] });
    }
  };
  useWebSocketEvent<EndpointEventPayload>(WebSocketEventType.ENDPOINT_CREATED, invalidateEndpoints);
  useWebSocketEvent<EndpointEventPayload>(WebSocketEventType.ENDPOINT_UPDATED, invalidateEndpoints);

  // === Account suspension -> refetch access/banner state ===
  useWebSocketEvent(WebSocketEventType.ACCOUNT_SUSPENDED, () => {
    void queryClient.invalidateQueries({ queryKey: ['banners'] });
    void queryClient.invalidateQueries({ queryKey: ['billing'] });
    void queryClient.invalidateQueries({ queryKey: ['currentUser'] });
  });

  // === Controller integrations ===
  const invalidateControllers = () => {
    void queryClient.invalidateQueries({ queryKey: ['controllers'] });
    void queryClient.invalidateQueries({ queryKey: ['controller'] });
  };
  useWebSocketEvent(WebSocketEventType.CONTROLLER_INTEGRATION_CREATED, invalidateControllers);
  useWebSocketEvent(WebSocketEventType.CONTROLLER_INTEGRATION_UPDATED, invalidateControllers);
  useWebSocketEvent(WebSocketEventType.CONTROLLER_INTEGRATION_DELETED, invalidateControllers);
  useWebSocketEvent(WebSocketEventType.CONTROLLER_SYNC_HEALTH_CHANGED, invalidateControllers);

  // === Alert definitions & overrides ===
  const invalidateAlertDefinitions = () => {
    void queryClient.invalidateQueries({ queryKey: ['alert-definitions'] });
    void queryClient.invalidateQueries({ queryKey: ['alert-definition'] });
    void queryClient.invalidateQueries({ queryKey: ['alertDefinitions'] });
  };
  useWebSocketEvent(WebSocketEventType.ALERT_DEFINITION_CREATED, invalidateAlertDefinitions);
  useWebSocketEvent(WebSocketEventType.ALERT_DEFINITION_UPDATED, invalidateAlertDefinitions);
  useWebSocketEvent(WebSocketEventType.ALERT_DEFINITION_DELETED, invalidateAlertDefinitions);
  useWebSocketEvent(WebSocketEventType.ALERT_DEFINITION_EXECUTED, invalidateAlertDefinitions);
  const invalidateAlertOverrides = () => {
    void queryClient.invalidateQueries({ queryKey: ['alert-overrides'] });
  };
  useWebSocketEvent(WebSocketEventType.ALERT_OVERRIDE_CREATED, invalidateAlertOverrides);
  useWebSocketEvent(WebSocketEventType.ALERT_OVERRIDE_UPDATED, invalidateAlertOverrides);
  useWebSocketEvent(WebSocketEventType.ALERT_OVERRIDE_DELETED, invalidateAlertOverrides);

  // === Incidents ===
  // Backend emits incident.created / incident.updated (payload: { incidentId }).
  // Broad-invalidate so the incident list (['incidents', ...] incl. device/region),
  // the detail (['incident', id]), and the event timeline (['incident-events', id])
  // all refetch. Note ['incident'] does NOT match ['incidents', ...] (distinct keys),
  // so both are invalidated explicitly.
  const invalidateIncidents = () => {
    void queryClient.invalidateQueries({ queryKey: ['incidents'] });
    void queryClient.invalidateQueries({ queryKey: ['incident'] });
    void queryClient.invalidateQueries({ queryKey: ['incident-events'] });
  };
  useWebSocketEvent(WebSocketEventType.INCIDENT_CREATED, invalidateIncidents);
  useWebSocketEvent(WebSocketEventType.INCIDENT_UPDATED, invalidateIncidents);

  // === Alert firing state ===
  // alert.triggered / alert.resolved signal an alert condition starting/clearing.
  // (No backend publishes these yet — the live path today is incident.* above —
  // but wire them so they refetch the affected surfaces if/when emitted, rather
  // than falling through to the "unhandled" log.) A firing alert shows up in
  // device health and alert-run history, and typically drives an incident.
  const invalidateAlertState = () => {
    void queryClient.invalidateQueries({ queryKey: ['device-health'] });
    void queryClient.invalidateQueries({ queryKey: ['task-executions'] });
    void queryClient.invalidateQueries({ queryKey: ['incidents'] });
    void queryClient.invalidateQueries({ queryKey: ['incident'] });
  };
  useWebSocketEvent(WebSocketEventType.ALERT_TRIGGERED, invalidateAlertState);
  useWebSocketEvent(WebSocketEventType.ALERT_RESOLVED, invalidateAlertState);

  // === RBAC: teams, roles, invitations ===
  const invalidateTeams = () => {
    void queryClient.invalidateQueries({ queryKey: ['teams'] });
    void queryClient.invalidateQueries({ queryKey: ['team-members'] });
    void queryClient.invalidateQueries({ queryKey: ['team-roles'] });
  };
  useWebSocketEvent(WebSocketEventType.TEAM_CREATED, invalidateTeams);
  useWebSocketEvent(WebSocketEventType.TEAM_UPDATED, invalidateTeams);
  useWebSocketEvent(WebSocketEventType.TEAM_DELETED, invalidateTeams);
  useWebSocketEvent(WebSocketEventType.TEAM_MEMBERSHIP_CHANGED, invalidateTeams);
  const invalidateRoles = () => {
    void queryClient.invalidateQueries({ queryKey: ['roles'] });
    void queryClient.invalidateQueries({ queryKey: ['role-permissions'] });
  };
  useWebSocketEvent(WebSocketEventType.ROLE_CREATED, invalidateRoles);
  useWebSocketEvent(WebSocketEventType.ROLE_UPDATED, invalidateRoles);
  useWebSocketEvent(WebSocketEventType.ROLE_DELETED, invalidateRoles);
  useWebSocketEvent(WebSocketEventType.ROLE_PERMISSIONS_CHANGED, invalidateRoles);
  const invalidateInvitations = () => {
    void queryClient.invalidateQueries({ queryKey: ['invitations'] });
  };
  useWebSocketEvent(WebSocketEventType.USER_INVITATION_CREATED, invalidateInvitations);
  useWebSocketEvent(WebSocketEventType.USER_INVITATION_UPDATED, invalidateInvitations);
  useWebSocketEvent(WebSocketEventType.USER_INVITATION_DELETED, invalidateInvitations);

  // === Transports / provisioning tokens / organization / org security policy ===
  const invalidateTransports = () => {
    void queryClient.invalidateQueries({ queryKey: ['transportDefinitions'] });
  };
  useWebSocketEvent(WebSocketEventType.TRANSPORT_DEFINITION_CREATED, invalidateTransports);
  useWebSocketEvent(WebSocketEventType.TRANSPORT_DEFINITION_UPDATED, invalidateTransports);
  useWebSocketEvent(WebSocketEventType.TRANSPORT_DEFINITION_DELETED, invalidateTransports);
  const invalidateProvisioningTokens = () => {
    void queryClient.invalidateQueries({ queryKey: ['provisioningTokens'] });
  };
  useWebSocketEvent(WebSocketEventType.PROVISIONING_TOKEN_CREATED, invalidateProvisioningTokens);
  useWebSocketEvent(WebSocketEventType.PROVISIONING_TOKEN_UPDATED, invalidateProvisioningTokens);
  useWebSocketEvent(WebSocketEventType.ORGANIZATION_UPDATED, () => {
    void queryClient.invalidateQueries({ queryKey: ['contexts'] });
    void queryClient.invalidateQueries({ queryKey: ['currentUser'] });
  });
  const invalidateOrgSecurityPolicy = () => {
    void queryClient.invalidateQueries({ queryKey: ['security-policy'] });
  };
  useWebSocketEvent(WebSocketEventType.SECURITY_POLICY_UPDATED, invalidateOrgSecurityPolicy);
  useWebSocketEvent(WebSocketEventType.SECURITY_POLICY_DELETED, invalidateOrgSecurityPolicy);

  // === Change management (the previously-unwired TODO) ===
  const invalidateCmQueries = (p: CmEventPayload) => {
    void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
    void queryClient.invalidateQueries({ queryKey: ['change-management'] });
    if (p.resourceId) {
      void queryClient.invalidateQueries({ queryKey: ['change-management', p.resourceId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-events', p.resourceId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-plans', p.resourceId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-owners', p.resourceId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-approvers', p.resourceId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-watchers', p.resourceId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-assignees', p.resourceId] });
      // Affected devices are (re)snapshotted at submit, which is a state change.
      void queryClient.invalidateQueries({ queryKey: ['cm-affected-devices', p.resourceId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-alerts', p.resourceId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-snapshots', p.resourceId] });
      void queryClient.invalidateQueries({ queryKey: ['cm-restores', p.resourceId] });
    }
  };
  useWebSocketEvent<CmEventPayload>(WebSocketEventType.CM_CREATED, invalidateCmQueries);
  useWebSocketEvent<CmEventPayload>(WebSocketEventType.CM_CREATED_AUTOMATED, invalidateCmQueries);
  useWebSocketEvent<CmEventPayload>(WebSocketEventType.CM_UPDATED, invalidateCmQueries);
  useWebSocketEvent<CmEventPayload>(WebSocketEventType.CM_STATE_CHANGED, invalidateCmQueries);
  useWebSocketEvent<CmEventPayload>(WebSocketEventType.CM_RETRIED, invalidateCmQueries);
  useWebSocketEvent<CmEventPayload>(WebSocketEventType.CM_COMMENT_ADDED, invalidateCmQueries);
  useWebSocketEvent<CmEventPayload>(WebSocketEventType.CM_PLAN_ATTACHED, invalidateCmQueries);
  useWebSocketEvent<CmEventPayload>(WebSocketEventType.CM_PLAN_DETACHED, invalidateCmQueries);
  useWebSocketEvent<CmEventPayload>(WebSocketEventType.CM_PLAN_REORDERED, invalidateCmQueries);

  useMspCacheSync();
}

/**
 * Smallest gap between two MSP rollup refetches, per tab.
 *
 * The customer rollup is one statement for the whole book, so a single refetch is
 * cheap — but the events that move it arrive in bursts, not singly. One gateway
 * reconnecting or one discovery pass emits a `device.status.changed` per device
 * within a few seconds, and every one of them changes the same rollup.
 */
const MSP_COALESCE_MS = 5_000;

/**
 * Keeps the MSP customer surfaces live from events that already flow.
 *
 * The dashboard is a portfolio board an MSP leaves open — but its query had no
 * `refetchInterval` and no realtime wiring, so it only updated on mount, on
 * window focus, or when someone pressed Refresh. A client could go critical and
 * the board would keep saying "healthy" until it was reloaded by hand.
 *
 * Nothing new is published for this. Every event below already goes to
 * `frontend.organization.{org_id}.{type}` unconditionally (the backend fans out
 * to the org topic as well as region and division), and the app is already
 * subscribed org-wide via `useOrganizationSubscription`. These events were
 * already arriving and simply weren't invalidating the MSP keys.
 *
 * Cost: `invalidateQueries` defaults to `refetchType: 'active'`, so a tab with no
 * MSP surface mounted marks the keys stale and issues no request at all. When one
 * *is* open, the coalescer below bounds it to one rollup query per
 * `MSP_COALESCE_MS` however many events land.
 */
function useMspCacheSync() {
  const invalidateCustomerState = useInvalidateCustomerState();

  // `useInvalidateCustomerState` is stable, so the scheduler below is too —
  // `useWebSocketEvent` has its handler in the effect deps, and an unstable
  // handler would re-subscribe on every render.

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    []
  );

  // Trailing-edge throttle, not a resetting debounce: the timer is armed by the
  // first event and every event inside the window is absorbed into that one fire.
  // A resetting debounce would postpone the refetch for as long as events kept
  // arriving, so a long discovery run — exactly when the board is most worth
  // watching — would never update until it finished.
  const scheduleMspInvalidate = useCallback(() => {
    if (timer.current !== null) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      invalidateCustomerState();
    }, MSP_COALESCE_MS);
  }, [invalidateCustomerState]);

  // Reachability and the two signals the health rules added: a failing collector
  // is the *earliest* evidence a client is in trouble — it fires while the device
  // still reads `online`, before the tick reaches its reachability verdict.
  useWebSocketEvent(WebSocketEventType.DEVICE_STATUS_CHANGED, scheduleMspInvalidate);
  useWebSocketEvent(WebSocketEventType.DEVICE_COLLECTOR_HEALTH_CHANGED, scheduleMspInvalidate);
  useWebSocketEvent(WebSocketEventType.GATEWAY_STATUS_CHANGED, scheduleMspInvalidate);
  useWebSocketEvent(WebSocketEventType.GATEWAY_ONLINE, scheduleMspInvalidate);
  useWebSocketEvent(WebSocketEventType.GATEWAY_OFFLINE, scheduleMspInvalidate);

  // Fleet size and what's under management.
  useWebSocketEvent(WebSocketEventType.DEVICE_CREATED, scheduleMspInvalidate);
  useWebSocketEvent(WebSocketEventType.DEVICE_DELETED, scheduleMspInvalidate);
  useWebSocketEvent(WebSocketEventType.DEVICE_UPDATED, scheduleMspInvalidate);
  useWebSocketEvent(WebSocketEventType.GATEWAY_UPDATED, scheduleMspInvalidate);
  useWebSocketEvent(WebSocketEventType.GATEWAY_DELETED, scheduleMspInvalidate);

  // The incident counts and worst-severity the board sorts on, and both feeds.
  useWebSocketEvent(WebSocketEventType.INCIDENT_CREATED, scheduleMspInvalidate);
  useWebSocketEvent(WebSocketEventType.INCIDENT_UPDATED, scheduleMspInvalidate);

  // Change-management counts per customer.
  useWebSocketEvent(WebSocketEventType.CM_STATE_CHANGED, scheduleMspInvalidate);
  useWebSocketEvent(WebSocketEventType.CM_COMPLETED, scheduleMspInvalidate);

  // Sites, and the roster itself.
  useWebSocketEvent(WebSocketEventType.REGION_CREATED, scheduleMspInvalidate);
  useWebSocketEvent(WebSocketEventType.REGION_DELETED, scheduleMspInvalidate);
  useWebSocketEvent(WebSocketEventType.DIVISION_CREATED, scheduleMspInvalidate);
  useWebSocketEvent(WebSocketEventType.DIVISION_DELETED, scheduleMspInvalidate);
}

/**
 * Hook for topology-specific cache sync
 * Use this in the Views page for real-time topology updates
 *
 * Invalidates topology queries when devices or connections change
 * in the specified region.
 *
 * @param regionId - The region ID to sync cache for
 */
/**
 * Collapse a burst of topology events into one refetch.
 *
 * A single device poll can emit several events in quick succession (status
 * changed, updated, then its connections), and every handler below invalidates
 * the same query key — so each message used to cost its own
 * `GET /api/topology`. The trailing debounce coalesces a burst; the max wait
 * bounds how long a *continuous* stream can starve the refetch, which a plain
 * debounce cannot do.
 */
const TOPOLOGY_INVALIDATE_DEBOUNCE_MS = 500;
const TOPOLOGY_INVALIDATE_MAX_WAIT_MS = 2000;

export function useTopologyCacheSync(regionId: string | null | undefined) {
  const queryClient = useQueryClient();

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstQueuedAtRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );

  // Helper to invalidate topology queries
  const invalidateTopology = () => {
    const now = Date.now();
    firstQueuedAtRef.current ??= now;

    // Never let a continuous event stream push the refetch out indefinitely:
    // once the oldest queued event is MAX_WAIT old, fire on this tick.
    const waited = now - firstQueuedAtRef.current;
    const delay = Math.max(0, Math.min(TOPOLOGY_INVALIDATE_DEBOUNCE_MS, TOPOLOGY_INVALIDATE_MAX_WAIT_MS - waited));

    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      firstQueuedAtRef.current = null;
      // Invalidate all topology queries - the query key includes regionId in fetchParams
      void queryClient.invalidateQueries({ queryKey: ['topology'] });
    }, delay);
  };

  /**
   * Does this event belong to the region we're showing?
   *
   * Read the scope from the payload OR the message envelope. The backend routes
   * every event with a `regionId` on the envelope; it now also mirrors it into the
   * payload (see `publish_notification`), which is what these payload types have
   * always declared. Checking only `payload.regionId` meant that whenever a
   * publisher omitted it the comparison was `undefined === regionId` — silently
   * false forever, disabling every handler below rather than failing loudly. The
   * fallback keeps this hook working regardless of which side carries the scope.
   */
  const inRegion = (payload: { regionId?: string } | undefined, message: { regionId?: string } | undefined) =>
    (payload?.regionId ?? message?.regionId) === regionId;

  // Device status changed -> Update topology to show new status
  useWebSocketEvent<DeviceStatusChangedPayload>(
    WebSocketEventType.DEVICE_STATUS_CHANGED,
    (payload, message) => {
      if (inRegion(payload, message)) {
        console.log('[Topology] Device status changed:', payload.deviceId, payload.newStatus);
        invalidateTopology();
      }
    },
    !!regionId
  );

  // Device created -> Add new device to topology
  useWebSocketEvent<DeviceCreatedPayload>(
    WebSocketEventType.DEVICE_CREATED,
    (payload, message) => {
      if (inRegion(payload, message)) {
        console.log('[Topology] Device created:', payload.deviceId);
        invalidateTopology();
      }
    },
    !!regionId
  );

  // Device deleted -> Remove device from topology
  useWebSocketEvent<DeviceDeletedPayload>(
    WebSocketEventType.DEVICE_DELETED,
    (payload, message) => {
      if (inRegion(payload, message)) {
        console.log('[Topology] Device deleted:', payload.deviceId);
        invalidateTopology();
      }
    },
    !!regionId
  );

  // Device updated -> Update device properties in topology
  useWebSocketEvent<DeviceUpdatedPayload>(
    WebSocketEventType.DEVICE_UPDATED,
    (payload, message) => {
      if (inRegion(payload, message)) {
        console.log('[Topology] Device updated:', payload.deviceId);
        invalidateTopology();
      }
    },
    !!regionId
  );

  // Device discovered -> Add discovered device to topology
  useWebSocketEvent<DeviceDiscoveredPayload>(
    WebSocketEventType.DEVICE_DISCOVERED,
    (payload, message) => {
      if (inRegion(payload, message)) {
        console.log('[Topology] Device discovered:', payload.deviceId);
        invalidateTopology();
      }
    },
    !!regionId
  );

  // Connection discovered -> Add new edge to topology
  useWebSocketEvent<ConnectionDiscoveredPayload>(
    WebSocketEventType.CONNECTION_DISCOVERED,
    (payload, message) => {
      if (inRegion(payload, message)) {
        console.log('[Topology] Connection discovered:', payload.connectionId);
        invalidateTopology();
      }
    },
    !!regionId
  );

  // Connection removed -> Remove edge from topology
  useWebSocketEvent<ConnectionRemovedPayload>(
    WebSocketEventType.CONNECTION_REMOVED,
    (payload, message) => {
      if (inRegion(payload, message)) {
        console.log('[Topology] Connection removed:', payload.connectionId);
        invalidateTopology();
      }
    },
    !!regionId
  );

  // Topology edge changed -> LLDP adjacency added/folded/refined/pruned in this
  // region. This is the event the backend actually emits (the connection.* ones
  // above are legacy and never fire), so it's what keeps the live graph current.
  useWebSocketEvent<TopologyEdgeChangedPayload>(
    WebSocketEventType.TOPOLOGY_EDGE_CHANGED,
    (payload, message) => {
      if (inRegion(payload, message)) {
        console.log('[Topology] Edge changed:', payload.edgesChanged);
        invalidateTopology();
      }
    },
    !!regionId
  );

  // Wireless client roam/associate -> the AP's badge/edge in the graph may move.
  useWebSocketEvent<DeviceScopedEventPayload>(
    WebSocketEventType.WIRELESS_CLIENT_CHANGED,
    (payload, message) => {
      if (inRegion(payload, message)) {
        invalidateTopology();
      }
    },
    !!regionId
  );
}
